import { Component, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, map, Observable, startWith } from 'rxjs';
import { CurrentUserService } from 'src/app/user/user.service';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import {
  Brand,
  BrandBindingConflict,
  BrandBindingNameItem,
  BrandBindings
} from 'src/typings';
import { environment } from 'src/environments/environment';
import { BRAND_COUNTRY_OPTIONS } from '../brand-country-options';
import { BrandService } from '../brand.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent {
  @Input() set brand(payload: Brand | undefined) {
    this._brand = payload;
    this.patchBrandForm(payload);
  }

  get brand() {
    return this._brand;
  }

  private _brand: Brand | undefined;

  readonly countryOptions = BRAND_COUNTRY_OPTIONS;

  readonly ratingOptions = [1, 2, 3, 4, 5];

  profileFormGroup: UntypedFormGroup = this.fb.group({
    description: new UntypedFormControl(),
    oe: new UntypedFormControl(false),
    short_description: new UntypedFormControl(),
    country: new UntypedFormControl(),
    rating: new UntypedFormControl(null),
    logo: new UntypedFormControl(null)
  });

  filteredCountries$: Observable<string[]> = this.profileFormGroup.controls.country.valueChanges.pipe(
    startWith(''),
    map((value) => this.filterCountries(value))
  );

  bindings: BrandBindings = {
    currentNames: [],
    conflicts: []
  };

  loading = false;

  logoUploading = false;

  constructor(
    private brandService: BrandService,
    private currentUserService: CurrentUserService,
    private activateRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private fb: UntypedFormBuilder
  ) {
    this.activateRoute.params.subscribe((params) => {
      if (params.id) void this.loadBrandPage(Number(params.id));
    });
  }

  get pageTitle() {
    if (!this.brand) return 'Карточка бренда';
    return `${this.brand.id} - ${this.brand.description}`;
  }

  get logoName(): string | null {
    return this.profileFormGroup.controls.logo.value || null;
  }

  get logoPreviewUrl(): string | null {
    const logo = this.logoName;
    return logo ? `${environment.BACKEND}/image/${logo}` : null;
  }

  get currentNames(): BrandBindingNameItem[] {
    return this.bindings.currentNames || [];
  }

  get conflicts(): BrandBindingConflict[] {
    return this.bindings.conflicts || [];
  }

  async loadBrandPage(id: number) {
    this.loading = true;
    try {
      const [brand, bindings] = await Promise.all([
        firstValueFrom(this.brandService.getOne(id)),
        firstValueFrom(this.brandService.getBindings(id))
      ]);

      this.brand = brand;
      this.bindings = bindings;
    } catch (err) {
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось загрузить карточку бренда')
      );
    } finally {
      this.loading = false;
    }
  }

  async save() {
    if (!this.brand) {
      this.notificationService.warning('Нет производителя!');
      return;
    }
    if (!this.isCountryValueValid()) {
      this.notificationService.warning('Выберите страну из списка или очистите поле.');
      return;
    }

    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.brandService.update({
          id: this.brand.id,
          description: this.normalizeString(this.profileFormGroup.value.description) || undefined,
          short_description: this.normalizeString(this.profileFormGroup.value.short_description),
          oe: Boolean(this.profileFormGroup.value.oe),
          country: this.normalizeString(this.profileFormGroup.value.country),
          rating: this.normalizeRating(this.profileFormGroup.value.rating),
          logo: this.normalizeString(this.profileFormGroup.value.logo)
        })
      );

      this.brand = res;
      this.bindings = await firstValueFrom(this.brandService.getBindings(this.brand.id));
      this.notificationService.notice('Бренд сохранён.');
    } catch (err) {
      this.notificationService.warning(extractApiErrorMessage(err, 'Не удалось сохранить бренд'));
    } finally {
      this.loading = false;
    }
  }

  async onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;

    this.logoUploading = true;
    try {
      const uploadRes = await firstValueFrom(this.currentUserService.uploadFile(file));
      this.profileFormGroup.controls.logo.setValue(uploadRes.name);
      this.notificationService.notice('Логотип загружен. Не забудьте сохранить бренд.');
    } catch (err) {
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось загрузить логотип')
      );
    } finally {
      this.logoUploading = false;
      if (input) {
        input.value = '';
      }
    }
  }

  clearLogo() {
    this.profileFormGroup.controls.logo.setValue(null);
  }

  setRating(value: number) {
    this.profileFormGroup.controls.rating.setValue(value);
  }

  clearRating() {
    this.profileFormGroup.controls.rating.setValue(null);
  }

  isRatingActive(value: number) {
    const current = Number(this.profileFormGroup.controls.rating.value || 0);
    return current >= value;
  }

  getRatingLabel(value: number) {
    const labels = ['Низкий', 'Ниже среднего', 'Средний', 'Хороший', 'Топ'];
    return labels[value - 1] || `${value}/5`;
  }

  getCurrentNameTypeLabel(type: BrandBindingNameItem['type']) {
    switch (type) {
      case 'canonical':
        return 'Основное';
      case 'short':
        return 'Короткое';
      case 'alias':
      default:
        return 'Привязка';
    }
  }

  async handleBrandChanged(brand: Brand) {
    this.brand = brand;
    this.bindings = await firstValueFrom(this.brandService.getBindings(brand.id));
  }

  trackConflict(_index: number, conflict: BrandBindingConflict) {
    return conflict.id;
  }

  private patchBrandForm(payload: Brand | undefined) {
    this.profileFormGroup.patchValue(
      {
        description: payload?.description || '',
        oe: Boolean(payload?.oe),
        short_description: payload?.short_description || '',
        country: payload?.country || '',
        rating: payload?.rating ?? null,
        logo: payload?.logo ?? null
      },
      { emitEvent: true }
    );
  }

  private filterCountries(rawValue: unknown): string[] {
    const normalized = this.normalizeString(rawValue)?.toLowerCase() || '';
    if (!normalized) {
      return this.countryOptions.slice(0, 50);
    }

    return this.countryOptions
      .filter((country) => country.toLowerCase().includes(normalized))
      .slice(0, 50);
  }

  private normalizeString(value: unknown): string | null {
    if (value === undefined || value === null) return null;
    const normalized = String(value).trim();
    return normalized || null;
  }

  private normalizeRating(value: unknown): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    const normalized = Number(value);
    return Number.isInteger(normalized) && normalized >= 1 && normalized <= 5 ? normalized : null;
  }

  private isCountryValueValid(): boolean {
    const country = this.normalizeString(this.profileFormGroup.controls.country.value);
    return !country || this.countryOptions.includes(country);
  }
}
